import shapeTypes from '../constants/ShapeConstants';
import uuid from 'uuid';
import slideParserUtils from './utils/SlideParserUtils';
import configuration from '../constants/Configuration';
import appParameters from '../components/utils/AppParameters';

export function parseSlide(slideElement) {
    const metadata = parseSlideMetadata(slideElement);
    const svgData = parseSlideSVG(slideElement.getElementsByTagName('svg')[0]);

    return Object.assign({}, metadata, {content: svgData}, {initialized: false}, {time: configuration.SLIDE_DURATION});
}

export function parseSlideMetadata (slideElement) {
    const title = slideElement.getElementsByTagName('title')[0].textContent;
    return {
        title
    };
}

export function parseSlideSVG (svgElement) {
    const shapes = [];
    for (let shapeNode of Array.from(svgElement.childNodes)) {
        if (shapeNode.nodeType !== 1) {
            continue;
        }
        const shapeObject = parseShape(shapeNode);
        if (shapeObject) {
            shapes.push(shapeObject);
        }
    }
    return shapes;
}

export function parseShape (shapeNode) {
    const localName = shapeNode.localName;
    const insperaClassName = shapeNode.getAttribute('inspera:className');

    switch (true) {
        case (localName === 'g' && insperaClassName === shapeTypes.BACKGROUND) :
            return parseBackground(shapeNode);

        case (localName === 'image' && insperaClassName === shapeTypes.CHARACTER) :
            return parseCharacter(shapeNode);

        case (localName === 'image' && insperaClassName === shapeTypes.IMAGE) :
            return parseImage(shapeNode);

        case (localName === 'rect') :
            return parseRectangle(shapeNode);

        case (localName === 'ellipse') :
            return parseEllipse(shapeNode);

        case (localName === 'line') :
            return parseLine(shapeNode);

        case (localName === 'path' && insperaClassName === shapeTypes.CURVE) :
            return parseCurve(shapeNode);

        case (localName === 'path' && insperaClassName === shapeTypes.FREEHAND) :
            return parseFreehand(shapeNode);

        case (localName === 'g' && insperaClassName === shapeTypes.TEXTBOX) :
            return parseTextBox(shapeNode);

        case (localName === 'g' && insperaClassName === shapeTypes.SPEAK_BUBBLE) :
            return parseSpeakBubble(shapeNode);

        case (localName === 'g' && insperaClassName === shapeTypes.SCREAM_BUBBLE) :
            return parseScreamBubble(shapeNode);

        case (localName === 'g' && insperaClassName === shapeTypes.THOUGHT_BUBBLE) :
            return parseThoughtBubble(shapeNode);
    }
}

function offsetY() {
    return 0;
}

function parseStroke (shapeNode) {
    const stroke = shapeNode.getAttribute('stroke');
    const strokeWidth = parseFloat(shapeNode.getAttribute('stroke-width'));
    const strokeOpacity = parseFloat(shapeNode.getAttribute('stroke-opacity'));

    return {
        stroke,
        strokeWidth,
        strokeOpacity
    };
}

function parseFill (shapeNode) {
    var fill = shapeNode.getAttribute('fill');
    var fillOpacity = parseFloat(shapeNode.getAttribute('fill-opacity'));

    fillOpacity = 1;

    if (fill == undefined || fill == 'none')
        fill = '#ffffff';

    return {
        fill,
        fillOpacity
    };
}

function parseGenericShape (shapeNode) {
    const x = parseFloat(shapeNode.getAttribute('x'));
    const y = parseFloat(shapeNode.getAttribute('y')) + offsetY();
    const width = parseFloat(shapeNode.getAttribute('width'));
    const height = parseFloat(shapeNode.getAttribute('height'));
    var opacity = parseFloat(shapeNode.getAttribute('opacity'));
    const filter = shapeNode.getAttribute('filter');
    const rotation = slideParserUtils.getRotation(shapeNode.getAttribute('transform'));
    var isLocked = shapeNode.getAttribute('inspera:isLocked');
    const Z = Number(shapeNode.getAttribute('inspera:Z'));

    const isSelected = false;

    const strokeData = parseStroke(shapeNode);
    const fillData = parseFill(shapeNode);

    if (opacity == undefined || isNaN(opacity))
        opacity = 1;

    if (isLocked == undefined || isNaN(isLocked) || isLocked == '')
        isLocked = false;

    const data = {
        uuid: uuid.v4(),
        x,
        y,
        width,
        height,
        opacity,
        filter,
        rotation,
        isLocked,
        Z,
        isSelected
    };

    return Object.assign({}, strokeData, fillData, data);
}

function parseFreehand (shapeNode) {
    const strokeData = parseStroke(shapeNode);
    const type = shapeTypes.FREEHAND;
    const Z = Number(shapeNode.getAttribute('inspera:Z'));
    const d = shapeNode.getAttribute('d');
    var opacity = parseFloat(shapeNode.getAttribute('opacity'));
    const filter = shapeNode.getAttribute('filter');
    const isLocked = shapeNode.getAttribute('inspera:isLocked');
    const rotation = slideParserUtils.getRotation(shapeNode.getAttribute('transform'));

    const isSelected = false;

    const globalPathPoints = slideParserUtils.dataToPoints(d);
    const properties = slideParserUtils.getFreehandSize(globalPathPoints);
    const pathPoints = slideParserUtils.converToLocalPathPoints(globalPathPoints, properties);
    const x = properties.x;
    const y = properties.y + offsetY();
    const width = properties.width;
    const height = properties.height;

    const horizontalFlip = shapeNode.getAttribute('inspera:isHorizontalFlip') == 'true' ? -1 : 1;
    const verticalFlip = shapeNode.getAttribute('inspera:isVerticalFlip') == 'true' ? -1 : 1;

    if (opacity == undefined || isNaN(opacity))
        opacity = 1;

    const data = {
        uuid: uuid.v4(),
        type,
        pathPoints,
        Z,
        opacity,
        filter,
        isLocked,
        rotation,
        x,
        y,
        width,
        height,
        isSelected,
        horizontalFlip,
        verticalFlip
    };

    return Object.assign({}, strokeData, data);
}

export function parseCurve (shapeNode) {
    const strokeData = parseStroke(shapeNode);
    const type = shapeTypes.CURVE;
    const Z = Number(shapeNode.getAttribute('inspera:Z'));
    const d = shapeNode.getAttribute('d');
    var opacity = parseFloat(shapeNode.getAttribute('opacity'));
    const filter = shapeNode.getAttribute('filter');
    const isLocked = shapeNode.getAttribute('inspera:isLocked');

    const isSelected = false;

    const pathPoints = slideParserUtils.dataToToughtPoints(d);
    const x1 = pathPoints[0].x;
    const y1 = pathPoints[0].y + offsetY();
    const x2 = pathPoints[2].x;
    const y2 = pathPoints[2].y + offsetY();
    const cx = pathPoints[1].x;
    const cy = pathPoints[1].y + offsetY();

    if (opacity == undefined || isNaN(opacity))
        opacity = 1;

    const data = {
        uuid: uuid.v4(),
        type,
        x1,
        y1,
        x2,
        y2,
        cx,
        cy,
        Z,
        opacity,
        filter,
        isLocked,
        isSelected
    };

    return Object.assign({}, strokeData, data);
}

export function parseLine (shapeNode) {
    const strokeData = parseStroke(shapeNode);
    const type = shapeTypes.LINE;
    const x1 = Number(shapeNode.getAttribute('x1'));
    const y1 = Number(shapeNode.getAttribute('y1')) + offsetY();
    const x2 = Number(shapeNode.getAttribute('x2'));
    const y2 = Number(shapeNode.getAttribute('y2')) + offsetY();
    const Z = Number(shapeNode.getAttribute('inspera:Z'));
    var opacity = parseFloat(shapeNode.getAttribute('opacity'));
    const filter = shapeNode.getAttribute('filter');
    const isLocked = shapeNode.getAttribute('inspera:isLocked');

    const isSelected = false;

    if (opacity == undefined || isNaN(opacity))
        opacity = 1;

    const data = {
        uuid: uuid.v4(),
        type,
        x1,
        y1,
        x2,
        y2,
        Z,
        opacity,
        filter,
        isLocked,
        isSelected
    };

    return Object.assign({}, strokeData, data);
}

export function parseEllipse (shapeNode) {
    const genericData = parseGenericShape(shapeNode);

    const type = shapeTypes.ELLIPSE;
    const cx = Number(shapeNode.getAttribute('cx'));
    const cy = Number(shapeNode.getAttribute('cy')) + offsetY();
    const rx = Number(shapeNode.getAttribute('rx'));
    const ry = Number(shapeNode.getAttribute('ry'));

    const width = rx * 2;
    const height = ry * 2;
    const x = Number((cx - rx).toFixed(2));
    const y = Number((cy - ry).toFixed(2));

    const data = {
        type,
        rx,
        ry,
        cx,
        cy,
        x,
        y,
        width,
        height
    };

    return Object.assign({}, genericData, data);
}

function parseTextBox (shapeNode) {
    const textArea = shapeNode.getElementsByTagName('textArea')[0];
    const rect = shapeNode.getElementsByTagName('rect')[0];

    const x = parseFloat(textArea.getAttribute('x'));
    const y = parseFloat(textArea.getAttribute('y')) + offsetY();
    const width = parseFloat(textArea.getAttribute('width'));
    const height = parseFloat(textArea.getAttribute('height'));

    const type = shapeTypes.TEXTBOX;
    var opacity = parseFloat(shapeNode.getAttribute('opacity'));
    const filter = shapeNode.getAttribute('filter');
    const isLocked = shapeNode.getAttribute('inspera:isLocked');
    const Z = shapeNode.getAttribute('inspera:Z');
    const backgroundVisible = true;

    const isSelected = false;

    const strokeData = parseStroke(rect);
    const fillData = parseFill(rect);
    const textElements = slideParserUtils.getTextProperties(textArea);

    if (opacity == undefined || isNaN(opacity))
        opacity = 1;

    const data = {
        uuid: uuid.v4(),
        x,
        y,
        width,
        height,
        type,
        opacity,
        filter,
        isLocked,
        Z,
        backgroundVisible,
        isSelected
    };

    return Object.assign({}, strokeData, fillData, textElements, data);
}

function parseBubble (shapeNode) {
    const textArea = shapeNode.getElementsByTagName('textArea')[0];
    const path = shapeNode.getElementsByTagName('path')[0];

    const strokeData = parseStroke(path);
    const fillData = parseFill(path);

    const x = parseFloat(textArea.getAttribute('x'));
    const y = parseFloat(textArea.getAttribute('y')) + offsetY();
    const width = parseFloat(textArea.getAttribute('width'));
    const height = parseFloat(textArea.getAttribute('height'));

    var opacity = parseFloat(shapeNode.getAttribute('opacity'));
    const filter = shapeNode.getAttribute('filter');
    const isLocked = shapeNode.getAttribute('inspera:isLocked');
    const Z = shapeNode.getAttribute('inspera:Z');
    var backgroundVisible = true;

    const isSelected = false;

    const cx = parseFloat(shapeNode.getAttribute('inspera:controlPointX'));
    const cy = parseFloat(shapeNode.getAttribute('inspera:controlPointY'));
    const textElements = slideParserUtils.getTextProperties(textArea);

    if (opacity == undefined || isNaN(opacity))
        opacity = 1;

    const data = {
        uuid: uuid.v4(),
        x,
        y,
        width,
        height,
        opacity,
        filter,
        isLocked,
        Z,
        backgroundVisible,
        cx,
        cy,
        isSelected
    };

    return Object.assign({}, strokeData, fillData, textElements, data);
}

function parseSpeakBubble (shapeNode) {
    const bubbleData = parseBubble(shapeNode);
    const type = shapeTypes.SPEAK_BUBBLE;

    const data = {
        type
    };

    return Object.assign({}, bubbleData, data);
}

function parseScreamBubble (shapeNode) {
    const bubbleData = parseBubble(shapeNode);
    const type = shapeTypes.SCREAM_BUBBLE;

    const data = {
        type
    };

    return Object.assign({}, bubbleData, data);
}

function parseThoughtBubble (shapeNode) {
    const bubbleData = parseBubble(shapeNode);
    const type = shapeTypes.THOUGHT_BUBBLE;

    const data = {
        type
    };

    return Object.assign({}, bubbleData, data);
}

export function parseRectangle (shapeNode) {
    const genericData = parseGenericShape(shapeNode);

    const type = shapeTypes.RECTANGLE;
    const data = {
        type
    };

    return Object.assign({}, genericData, data);
}

export function parseImage (shapeNode) {
    var genericData = parseGenericShape(shapeNode);

    const type = shapeTypes.IMAGE;
    const logicalName = shapeNode.getAttribute('inspera:logicalName');
    const horizontalFlip = shapeNode.getAttribute('inspera:isHorizontalFlip') == 'true' ? -1 : 1;
    const verticalFlip = shapeNode.getAttribute('inspera:isVerticalFlip') == 'true' ? -1 : 1;

    const imageType = shapeNode.getAttribute('inspera:imageType');
    const src = shapeNode.getAttribute('inspera:src');

    let host = '';

    const contentItemId = shapeNode.getAttribute('inspera:contentItemId');
    var xlinkHref = `${host}/file/ci/${contentItemId}/img`;

    if ((imageType && imageType.indexOf('swf') != -1)  || (src && src.indexOf('swf') != -1))
        xlinkHref = appParameters.SVG_URL() + `${logicalName}_.svg`;

    genericData.fillOpacity = 0;

    const imageData = {
        type,
        logicalName,
        xlinkHref,
        horizontalFlip,
        verticalFlip
    };

    return Object.assign({}, genericData, imageData);
}

export function parseCharacter(shapeNode) {
    var genericData = parseGenericShape(shapeNode);

    const type = shapeTypes.CHARACTER;
    const logicalName = shapeNode.getAttribute('inspera:logicalName');
    const frameLabel = shapeNode.getAttribute('inspera:frameLabel');
    const xlinkHref = appParameters.SVG_URL() + `${logicalName}_${frameLabel}.svg`;
    const horizontalFlip = shapeNode.getAttribute('inspera:isHorizontalFlip') == 'true' ? -1 : 1;
    const verticalFlip = shapeNode.getAttribute('inspera:isVerticalFlip') == 'true' ? -1 : 1;

    const moods = []; // TO DO: get correct moods
    genericData.fillOpacity = 0;

    const characterData = {
        type,
        logicalName,
        frameLabel,
        xlinkHref,
        moods,
        horizontalFlip,
        verticalFlip
    };

    return Object.assign({}, genericData, characterData);
}

export function parseBackground (shapeNode) {
    const type = shapeTypes.BACKGROUND;
    const hotSpot = shapeNode.getAttribute('inspera:hotSpot');

    const imageNode = shapeNode.getElementsByTagName('image')[0];
    const logicalName = imageNode.getAttribute('inspera:logicalName');
    const frameLabel = imageNode.getAttribute('inspera:frameLabel');
    const xlinkHref = appParameters.SVG_URL() + `${logicalName}_${frameLabel}.svg`;

    const fillRect = shapeNode.getElementsByTagName('rect')[0];
    const fill = fillRect.getAttribute('fill');
    var fillOpacity =Number(fillRect.getAttribute('fill-opacity'));

    if (fillOpacity == undefined || fillOpacity == NaN)
        fillOpacity = 1;

    const strokeRect = shapeNode.getElementsByTagName('rect')[1];
    const stroke = strokeRect.getAttribute('stroke');
    const strokeOpacity = Number(strokeRect.getAttribute('stroke-opacity'));
    const strokeWidth = Number(strokeRect.getAttribute('stroke-width'));

    const moods = []; // TO DO: get correct moods

    return {
        uuid: uuid.v4(),
        type,
        logicalName,
        frameLabel,
        xlinkHref,
        hotSpot,
        moods,
        fill,
        fillOpacity,
        stroke,
        strokeOpacity,
        strokeWidth
    };
}



/** WEBPACK FOOTER **
 ** ./src/parser/CartoonistSlideParser.js
 **/